/**
 * GuidePage - Venue Owner Guide
 *
 * Public page (no auth required) with step-by-step instructions
 * for venue owners on how to use their dashboard.
 * Linked from welcome emails sent after venue claim approval.
 *
 * Uses accordion sections for easy scanning on mobile.
 */

import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import './GuidePage.css';

function GuidePage() {
  const [openSection, setOpenSection] = useState('getting-started');

  const toggle = (id) => {
    setOpenSection(prev => prev === id ? null : id);
  };

  const isOpen = (id) => openSection === id;

  return (
    <div className="gd-page">
      <SEOHead
        title="Venue Owner Guide - Off the Clock Spots"
        description="Step-by-step guide for venue owners to manage their listing on Off the Clock Spots."
        noindex
      />

      <header className="gd-header">
        <h1>Venue Owner Guide</h1>
        <p>Tap any section below to learn more</p>
      </header>

      <div className="gd-container">

        {/* Getting Started */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('getting-started') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('getting-started')}>
            <span className="gd-accordion-icon">🚀</span>
            <span className="gd-accordion-title">Getting Started</span>
            <span className="gd-accordion-arrow">{isOpen('getting-started') ? '−' : '+'}</span>
          </button>
          {isOpen('getting-started') && (
            <div className="gd-accordion-body">
              <p>
                Welcome! Here's how to log in for the first time and find your way around.
              </p>
              <ol className="gd-steps">
                <li className="gd-step">
                  <span className="gd-step-number">1</span>
                  <div className="gd-step-content">
                    <strong>Open your login link</strong>
                    <p>
                      Check your welcome email for a link that looks like
                      {' '}<code>offtheclockspots.com/naperville/login</code>.
                      Tap it to go to the login page.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">2</span>
                  <div className="gd-step-content">
                    <strong>Sign in with your temporary password</strong>
                    <p>
                      Enter the email you used to claim your venue and the
                      temporary password from your welcome email. You can tap
                      "Show" next to the password field to see what you're typing.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">3</span>
                  <div className="gd-step-content">
                    <strong>Choose your own password</strong>
                    <p>
                      You'll be asked to pick a new password right away. Make it
                      at least 8 characters. Your browser should offer to remember
                      it for you — we recommend saying yes!
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">4</span>
                  <div className="gd-step-content">
                    <strong>You're in!</strong>
                    <p>
                      You'll land on your dashboard — your home base for managing
                      everything about your venue.
                    </p>
                  </div>
                </li>
              </ol>
              <div className="gd-tip">
                <span className="gd-tip-icon">💻</span>
                <span>For the best experience, we recommend using your dashboard on a computer or tablet. It works on your phone too, but a bigger screen makes it easier to manage your content.</span>
              </div>
            </div>
          )}
        </section>

        {/* Your Dashboard */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('dashboard') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('dashboard')}>
            <span className="gd-accordion-icon">📊</span>
            <span className="gd-accordion-title">Your Dashboard</span>
            <span className="gd-accordion-arrow">{isOpen('dashboard') ? '−' : '+'}</span>
          </button>
          {isOpen('dashboard') && (
            <div className="gd-accordion-body">
              <p>
                Your dashboard has four tabs across the top. Here's what each one does:
              </p>
              <div className="gd-tab-cards">
                <div className="gd-tab-card">
                  <div className="gd-tab-card-header">Venue Profile</div>
                  <p>Update your address, phone number, website, hours, photos, and more.</p>
                </div>
                <div className="gd-tab-card">
                  <div className="gd-tab-card-header">My Content</div>
                  <p>See your published deals, events, and specials. Request edits or removals.</p>
                </div>
                <div className="gd-tab-card">
                  <div className="gd-tab-card-header">Upload Content</div>
                  <p>Add new deals or events by uploading a photo or pasting text.</p>
                </div>
                <div className="gd-tab-card">
                  <div className="gd-tab-card-header">Analytics</div>
                  <p>See how many people are viewing and clicking on your venue.</p>
                </div>
              </div>
              <div className="gd-tip">
                <span className="gd-tip-icon">💡</span>
                <span>First time? Start with <strong>Venue Profile</strong> to make sure your info is correct, then head to <strong>Upload Content</strong> to add your deals.</span>
              </div>
            </div>
          )}
        </section>

        {/* Updating Your Profile */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('profile') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('profile')}>
            <span className="gd-accordion-icon">✏️</span>
            <span className="gd-accordion-title">Updating Your Profile</span>
            <span className="gd-accordion-arrow">{isOpen('profile') ? '−' : '+'}</span>
          </button>
          {isOpen('profile') && (
            <div className="gd-accordion-body">
              <p>
                Go to the <strong>Venue Profile</strong> tab to keep your info up to date.
                You can change:
              </p>
              <ul className="gd-features">
                <li className="gd-feature">
                  <span className="gd-feature-icon">&#10003;</span>
                  <span>Phone number and website</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">&#10003;</span>
                  <span>Restaurant type (Bar, Brewery, Restaurant, etc.)</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">&#10003;</span>
                  <span>Description, Facebook page, and Instagram URL</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">&#10003;</span>
                  <span>Photos and thumbnail image</span>
                </li>
              </ul>
              <div className="gd-tip">
                <span className="gd-tip-icon">💡</span>
                <span>Profile changes take effect right away — no approval needed. Silver members get their phone number and social icons displayed on their public listing.</span>
              </div>
            </div>
          )}
        </section>

        {/* Adding New Content */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('adding') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('adding')}>
            <span className="gd-accordion-icon">📝</span>
            <span className="gd-accordion-title">Adding Deals, Events & Specials</span>
            <span className="gd-accordion-arrow">{isOpen('adding') ? '−' : '+'}</span>
          </button>
          {isOpen('adding') && (
            <div className="gd-accordion-body">
              <p>
                Go to the <strong>Upload Content</strong> tab. There are several ways to add content:
              </p>
              <div className="gd-method-cards">
                <div className="gd-method-card gd-method-card-silver">
                  <div className="gd-method-icon">✨</div>
                  <strong>Add New Form</strong>
                  <span className="gd-silver-label">Silver</span>
                  <p>
                    Silver members can add deals, events, and specials directly
                    with a simple form. Pick a type, fill in the details, and
                    submit. Events can include an image too.
                  </p>
                </div>
                <div className="gd-method-card">
                  <div className="gd-method-icon">📷</div>
                  <strong>Upload a Photo</strong>
                  <p>
                    Take a picture of your menu board, flyer, or specials list.
                    We'll read it and pull out the details for you. Just review
                    and hit submit.
                  </p>
                </div>
                <div className="gd-method-card">
                  <div className="gd-method-icon">💬</div>
                  <strong>Paste Text</strong>
                  <p>
                    Copy text from a social media post, email, or anywhere else
                    and paste it in. We'll extract the times, prices, and event
                    info automatically.
                  </p>
                </div>
              </div>
              <p>Here's what you can add:</p>
              <ul className="gd-features">
                <li className="gd-feature">
                  <span className="gd-feature-icon">🍺</span>
                  <span><strong>Happy Hour Deals</strong> — Drink and food specials with days and times</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">🎵</span>
                  <span><strong>Events</strong> — Live music, trivia, karaoke, comedy, and more</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">🌮</span>
                  <span><strong>Daily Specials</strong> — Taco Tuesday, Wing Wednesday, and other recurring deals</span>
                </li>
              </ul>
              <div className="gd-tip">
                <span className="gd-tip-icon">💡</span>
                <span>Everything you submit gets a quick review before going live to make sure it looks great.</span>
              </div>
            </div>
          )}
        </section>

        {/* Editing & Removing Content */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('editing') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('editing')}>
            <span className="gd-accordion-icon">🔄</span>
            <span className="gd-accordion-title">Editing & Removing Content</span>
            <span className="gd-accordion-arrow">{isOpen('editing') ? '−' : '+'}</span>
          </button>
          {isOpen('editing') && (
            <div className="gd-accordion-body">
              <p>
                Need to change a deal's hours or remove an old event? Go to the
                {' '}<strong>My Content</strong> tab.
              </p>
              <ol className="gd-steps">
                <li className="gd-step">
                  <span className="gd-step-number">1</span>
                  <div className="gd-step-content">
                    <strong>Tap "Request Edit"</strong>
                    <p>
                      Find the item you want to change and tap "Request Edit."
                      A form will open with your current info already filled in.
                      Just change what you need — times, days, descriptions — and
                      submit. Changed fields will be highlighted so you can
                      double-check.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">2</span>
                  <div className="gd-step-content">
                    <strong>Watch for the status badge</strong>
                    <p>
                      After you submit, you'll see a yellow "Edit Pending" badge
                      on that item. This means your request is being reviewed.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">3</span>
                  <div className="gd-step-content">
                    <strong>Check back for updates</strong>
                    <p>
                      If approved, the changes go live automatically. If there's
                      an issue, you'll see a red "Edit Rejected" badge with a
                      note explaining why. You can then make adjustments and
                      submit again.
                    </p>
                  </div>
                </li>
              </ol>
              <div className="gd-tip">
                <span className="gd-tip-icon">💡</span>
                <span>Want to remove something entirely? Tap "Request Removal" instead. It goes through the same review process.</span>
              </div>
            </div>
          )}
        </section>

        {/* Analytics */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('analytics') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('analytics')}>
            <span className="gd-accordion-icon">📈</span>
            <span className="gd-accordion-title">Viewing Your Analytics</span>
            <span className="gd-accordion-arrow">{isOpen('analytics') ? '−' : '+'}</span>
          </button>
          {isOpen('analytics') && (
            <div className="gd-accordion-body">
              <p>
                The <strong>Analytics</strong> tab shows you how people are engaging
                with your venue — page views, clicks, and interactions.
              </p>
              <ul className="gd-features">
                <li className="gd-feature">
                  <span className="gd-feature-icon">📊</span>
                  <span><strong>Free plan</strong> — 7-day analytics overview</span>
                </li>
                <li className="gd-feature">
                  <span className="gd-feature-icon">📈</span>
                  <span><strong>Silver plan</strong> — 30-day trends and engagement data</span>
                </li>
              </ul>
              <p>
                Want to see more data? You can request an upgrade to Silver
                right from your dashboard.
              </p>
            </div>
          )}
        </section>

        {/* Your Plan & Upgrading */}
        <section className="gd-section">
          <button className={`gd-accordion ${isOpen('upgrading') ? 'gd-accordion-open' : ''}`} onClick={() => toggle('upgrading')}>
            <span className="gd-accordion-icon">⭐</span>
            <span className="gd-accordion-title">Your Plan & Upgrading</span>
            <span className="gd-accordion-arrow">{isOpen('upgrading') ? '−' : '+'}</span>
          </button>
          {isOpen('upgrading') && (
            <div className="gd-accordion-body">
              <p>
                Every venue starts on the <strong>Free plan</strong>. When you're
                ready for more, you can upgrade to Silver right from your dashboard.
              </p>
              <div className="gd-plan-comparison">
                <div className="gd-plan-card">
                  <div className="gd-plan-card-header gd-plan-free">Free</div>
                  <ul className="gd-plan-features">
                    <li>Profile management</li>
                    <li>Photo & text uploads</li>
                    <li>Edit & removal requests</li>
                    <li>7-day analytics</li>
                  </ul>
                </div>
                <div className="gd-plan-card gd-plan-card-highlight">
                  <div className="gd-plan-card-header gd-plan-silver">Silver</div>
                  <ul className="gd-plan-features">
                    <li>Everything in Free, plus:</li>
                    <li>Add New content form</li>
                    <li>Phone & social icons on listing</li>
                    <li>30-day analytics</li>
                    <li>Priority review</li>
                    <li>Featured badge</li>
                  </ul>
                </div>
              </div>
              <h4 className="gd-how-heading">How to upgrade:</h4>
              <ol className="gd-steps">
                <li className="gd-step">
                  <span className="gd-step-number">1</span>
                  <div className="gd-step-content">
                    <strong>Find "Your Plan" on your dashboard</strong>
                    <p>
                      Scroll down on your dashboard to the "Your Plan" card. It shows
                      your current tier and what's included.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">2</span>
                  <div className="gd-step-content">
                    <strong>Tap "Upgrade to Silver"</strong>
                    <p>
                      A confirmation window will open showing what you'll get. You
                      can add an optional message if you'd like.
                    </p>
                  </div>
                </li>
                <li className="gd-step">
                  <span className="gd-step-number">3</span>
                  <div className="gd-step-content">
                    <strong>We'll review and activate</strong>
                    <p>
                      Your request goes to our team. Once approved, your account is
                      upgraded automatically — just log out and back in to see your
                      new Silver features.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          )}
        </section>

        {/* Support */}
        <section className="gd-section gd-support">
          <h2><span className="gd-section-icon">💬</span> Need Help?</h2>
          <p>
            Questions? Issues? We're here for you. Reach out anytime.
          </p>
          <a href="mailto:support@offtheclockspots.com" className="gd-support-email">
            support@offtheclockspots.com
          </a>
        </section>

        <a href="/" className="gd-back-link">
          &larr; Back to Off the Clock Spots
        </a>

      </div>
    </div>
  );
}

export default GuidePage;
